import { type ApiClient, listQueryParams } from "./client";
import type { JobFilters, ListQuery, Page, QueueJob, QueueJobDetail, QueueSummary } from "./types";

/** Queues: domain operations the UI depends on. Implementations: HTTP (below) and mock (src/api/mock). */
export interface QueuesApi {
  list(): Promise<QueueSummary[]>;
  get(name: string): Promise<QueueSummary>;
  listJobs(name: string, query: ListQuery<JobFilters>): Promise<Page<QueueJob>>;
  getJob(name: string, jobId: string): Promise<QueueJobDetail>;
  retryJob(name: string, jobId: string): Promise<QueueJobDetail>;
  discardJob(name: string, jobId: string): Promise<void>;
  pause(name: string): Promise<QueueSummary>;
  resume(name: string): Promise<QueueSummary>;
}

export function createHttpQueuesApi(client: ApiClient): QueuesApi {
  return {
    list: async () => (await client.call<{ items: QueueSummary[] }>("listQueues")).items,
    get: (name) => client.call("getQueue", { params: { name } }),
    listJobs: (name, q) => client.call("listQueueJobs", { params: { name }, query: listQueryParams(q) }),
    getJob: (name, jobId) => client.call("getQueueJob", { params: { name, jobId } }),
    // Re-arms the failed job with a fresh AI run (see the contract's retryQueueJob summary).
    retryJob: (name, jobId) => client.call("retryQueueJob", { params: { name, jobId } }),
    discardJob: async (name, jobId) => {
      await client.call("discardQueueJob", { params: { name, jobId } });
    },
    pause: (name) => client.call("pauseQueue", { params: { name } }),
    resume: (name) => client.call("resumeQueue", { params: { name } }),
  };
}
